import { useNavigate } from "react-router-dom";
import "../../../../../Booking/DateSelector/DateSelector.scss";
import '../Variations/variations.scss';
import { FiCalendar, FiUsers, FiDollarSign, FiCheckCircle, FiChevronRight, FiClock, FiMapPin, FiStar } from "react-icons/fi";

const Variations = ({ variations, onSelect, formData, setFormData, setUser }) => {
    const variationsList = JSON.parse(variations);
    const navigate = useNavigate();

    // Function to calculate availability status
    const getAvailabilityStatus = (seats) => {
        if (seats <= 0) return { text: "Sold Out", color: "#ff4757", bg: "rgba(255, 71, 87, 0.1)" };
        if (seats <= 5) return { text: "Limited Seats", color: "#ffa502", bg: "rgba(255, 165, 2, 0.1)" };
        if (seats <= 15) return { text: "Few Seats", color: "#2ed573", bg: "rgba(46, 213, 115, 0.1)" };
        return { text: "Available", color: "#2ed573", bg: "rgba(46, 213, 115, 0.1)" };
    };

    // Function to format date range
    const formatDateRange = (dateRange) => {
        const dates = dateRange.split('-');
        if (dates.length === 2) {
            return `${dates[0].trim()} - ${dates[1].trim()}`;
        }
        return dateRange;
    };

    // Function to calculate group discount - Fixed as requested
    const calculateGroupDiscount = (price, seats) => {
        if (seats >= 6) {
            // $60 discount for 6-7 people
            const discountedPrice = price - 60;
            return discountedPrice > 0 ? discountedPrice : price; // Ensure price doesn't go negative
        }
        if (seats >= 4) {
            // $50 discount for 4-5 people
            const discountedPrice = price - 50;
            return discountedPrice > 0 ? discountedPrice : price;
        }
        return price; // No discount for less than 4 people
    };

    return (
        <div className="variations-container">
            <div className="variations-header">
                <div className="header-content">
                    <FiCalendar className="header-icon" />
                    <div className="header-text">
                        <h2>Available Dates & Prices</h2>
                        <p>Choose your preferred tour date and book your adventure</p>
                    </div>
                </div>
                <div className="header-stats">
                    <div className="stat-item">
                        <FiUsers />
                        <span>Group Discounts Available</span>
                    </div>
                    <div className="stat-item">
                        <FiCheckCircle />
                        <span>Best Price Guarantee</span>
                    </div>
                </div>
            </div>

            <div className="variations-grid">
                {variationsList.map((item, index) => {
                    const availability = getAvailabilityStatus(item.seatsAvailable);
                    // Calculate discounts for 4 and 6 people
                    const groupPrice4 = calculateGroupDiscount(item.price, 4);
                    const groupPrice6 = calculateGroupDiscount(item.price, 6);
                    const discount4 = item.price - groupPrice4;
                    const discount6 = item.price - groupPrice6;
                    
                    return (
                        <div 
                            key={index} 
                            className={`variation-card ${item.seatsAvailable <= 0 ? 'sold-out' : ''}`}
                            onClick={() => {
                                if (item.seatsAvailable > 0) {
                                    onSelect(item);
                                    setFormData({
                                        ...formData,
                                        date: item.dateRange,
                                        amount: item.price
                                    });
                                    setUser();
                                }
                            }}
                        >
                            {item.seatsAvailable <= 0 && (
                                <div className="sold-out-badge">Sold Out</div>
                            )}
                            
                            {item.seatsAvailable > 0 && item.seatsAvailable <= 5 && (
                                <div className="limited-badge">Limited Seats</div>
                            )}
                            
                            <div className="card-header">
                                <div className="date-info">
                                    <FiCalendar className="card-icon" />
                                    <div className="date-content">
                                        <h3>{formatDateRange(item.dateRange)}</h3>
                                    </div>
                                </div>
                                <div className="availability" style={{ 
                                    color: availability.color,
                                    backgroundColor: availability.bg
                                }}>
                                    <FiUsers />
                                    <span>{item.seatsAvailable} seats left</span>
                                </div>
                            </div>
                            
                            <div className="card-body">
                                <div className="price-section">
                                    <div className="price-info">
                                        <div className="regular-price">
                                            <span className="price-label">From</span>
                                            <span className="price-amount">${item.price}</span>
                                            <span className="price-per">per person</span>
                                        </div>
                                        
                                        {/* Show group discounts only if applicable */}
                                        {item.price > 50 && (
                                            <div className="group-discounts-container">
                                                <div className="group-discount">
                                                    <FiUsers className="discount-icon" />
                                                    <div className="discount-info">
                                                        <span className="discount-label">Group of 4:</span>
                                                        <span className="discount-price">${groupPrice4}</span>
                                                        <span className="discount-text">Save ${discount4}</span>
                                                    </div>
                                                </div>
                                                {item.price > 60 && (
                                                    <div className="group-discount">
                                                        <FiUsers className="discount-icon" />
                                                        <div className="discount-info">
                                                            <span className="discount-label">Group of 6+:</span>
                                                            <span className="discount-price">${groupPrice6}</span>
                                                            <span className="discount-text">Save ${discount6}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="features">
                                        <div className="feature">
                                            <FiCheckCircle />
                                            <span>Best Price Guarantee</span>
                                        </div>
                                        <div className="feature">
                                            <FiCheckCircle />
                                            <span>Flexible Booking</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="details-section">
                                    <div className="detail-item">
                                        <FiMapPin />
                                        <span>Departure: {item.departurePoint || "Islamabad"}</span>
                                    </div>
                                    <div className="detail-item">
                                        <FiStar />
                                        <span>Difficulty: {item.difficulty || "Moderate"}</span>
                                    </div>
                                    {item.highlights && (
                                        <div className="tour-highlights">
                                            <strong>Tour Highlights:</strong>
                                            <p>{item.highlights}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="card-footer">
                                {item.seatsAvailable > 0 ? (
                                    <>
                                        <button 
                                            className="select-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSelect(item);
                                                setFormData({
                                                    ...formData,
                                                    date: item.dateRange,
                                                    amount: item.price
                                                });
                                                setUser();
                                            }}
                                        >
                                            <span>Book Now</span>
                                            <FiChevronRight />
                                        </button>
                                        <button 
                                            className="enquire-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Add enquire functionality
                                            }}
                                        >
                                            Enquire
                                        </button>
                                    </>
                                ) : (
                                    <button className="notify-button" disabled>
                                        Notify When Available
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            
            <div className="variations-footer">
                <div className="footer-note">
                    <FiCheckCircle />
                    <span>All prices include taxes and fees. No hidden charges.</span>
                </div>
                <div className="footer-note">
                    <FiCheckCircle />
                    <span>Flexible cancellation policy available.</span>
                </div>
                <div className="contact-support">
                    <p>Need help choosing a date? <a href="#contact">Contact our travel experts</a></p>
                </div>
            </div>
        </div>
    );
};

export default Variations;